import type { Book } from '@/types/book'

export type BookCardContext =
  | 'library'
  | 'wishlist'
  | 'admin'
  | 'readonly'
  | 'full'
  | 'roulette'
  | 'roulette-result'

export interface BookCardActionConfig {
  // Status transitions allowed from this context
  allowedStatusTransitions: string[]

  // Quick actions available
  allowRating: boolean
  allowEdit: boolean
  allowDelete: boolean
  allowNotes: boolean

  // Status-specific quick actions
  showQuickStatusActions: boolean

  // Custom restrictions
  restrictedActions?: string[]
}

export interface BookCardLayoutConfig {
  // Elements to show/hide
  showQuickActionsMenu: boolean
  showStatusBadge: boolean
  showRating: boolean
  showGenres: boolean
  showReadingDates: boolean
  showISBN: boolean
  showNotes: boolean
  showNotesEditor: boolean

  // Selection state (for roulette)
  showSelectionIndicator: boolean
  selectionIndicatorPosition: 'left' | 'right'

  // Layout adjustments
  enforceUniformHeight: boolean
  compactMode: boolean

  // Image aspect ratio
  imageAspectRatio: 'square' | 'book' // square = 1:1, book = 3:4
}

export interface BookCardConfig {
  context: BookCardContext
  actions: BookCardActionConfig
  layout: BookCardLayoutConfig
}

// Predefined action configurations for different contexts
export const BOOK_CARD_ACTION_CONFIGS: Record<BookCardContext, BookCardActionConfig> = {
  // Main library - books you own (no wishlist since they're already owned)
  library: {
    allowedStatusTransitions: ['unread', 'reading', 'read'], // no wishlist - you already own the book
    allowRating: true,
    allowEdit: true,
    allowDelete: true,
    allowNotes: true,
    showQuickStatusActions: true,
  },

  // Wishlist page - clean actions menu, no status dropdown clutter
  wishlist: {
    allowedStatusTransitions: [], // no status transitions in dropdown - use smart actions instead
    allowRating: false, // usually don't rate wishlist items
    allowEdit: true,
    allowDelete: true,
    allowNotes: true,
    showQuickStatusActions: true, // but show smart quick actions
  },

  // Admin view - full control
  admin: {
    allowedStatusTransitions: ['unread', 'reading', 'read', 'wishlist'],
    allowRating: true,
    allowEdit: true,
    allowDelete: true,
    allowNotes: true,
    showQuickStatusActions: true,
  },

  // Read-only view - minimal interactions
  readonly: {
    allowedStatusTransitions: [],
    allowRating: false,
    allowEdit: false,
    allowDelete: false,
    allowNotes: false,
    showQuickStatusActions: false,
  },

  // Full control - all transitions available (for special cases)
  full: {
    allowedStatusTransitions: ['unread', 'reading', 'read', 'wishlist'],
    allowRating: true,
    allowEdit: true,
    allowDelete: true,
    allowNotes: true,
    showQuickStatusActions: true,
  },

  // Roulette selection - minimal view for selection
  roulette: {
    allowedStatusTransitions: [],
    allowRating: false,
    allowEdit: false,
    allowDelete: false,
    allowNotes: false,
    showQuickStatusActions: false,
  },

  // Roulette result - display only
  'roulette-result': {
    allowedStatusTransitions: [],
    allowRating: false,
    allowEdit: false,
    allowDelete: false,
    allowNotes: false,
    showQuickStatusActions: false,
  },
}

// Predefined layout configurations for different contexts
export const BOOK_CARD_LAYOUT_CONFIGS: Record<BookCardContext, BookCardLayoutConfig> = {
  // Main library - full experience
  library: {
    showQuickActionsMenu: true,
    showStatusBadge: true,
    showRating: true,
    showGenres: true,
    showReadingDates: true,
    showISBN: true,
    showNotes: true,
    showNotesEditor: true,
    showSelectionIndicator: false,
    selectionIndicatorPosition: 'right',
    enforceUniformHeight: false,
    compactMode: false,
    imageAspectRatio: 'square',
  },

  // Wishlist page - clean view
  wishlist: {
    showQuickActionsMenu: true,
    showStatusBadge: true,
    showRating: false,
    showGenres: true,
    showReadingDates: false,
    showISBN: false,
    showNotes: true,
    showNotesEditor: false,
    showSelectionIndicator: false,
    selectionIndicatorPosition: 'right',
    enforceUniformHeight: false,
    compactMode: false,
    imageAspectRatio: 'square',
  },

  // Admin view - full details
  admin: {
    showQuickActionsMenu: true,
    showStatusBadge: true,
    showRating: true,
    showGenres: true,
    showReadingDates: true,
    showISBN: true,
    showNotes: true,
    showNotesEditor: true,
    showSelectionIndicator: false,
    selectionIndicatorPosition: 'right',
    enforceUniformHeight: false,
    compactMode: false,
    imageAspectRatio: 'book',
  },

  // Read-only view - minimal
  readonly: {
    showQuickActionsMenu: false,
    showStatusBadge: true,
    showRating: true,
    showGenres: true,
    showReadingDates: false,
    showISBN: false,
    showNotes: true,
    showNotesEditor: false,
    showSelectionIndicator: false,
    selectionIndicatorPosition: 'right',
    enforceUniformHeight: false,
    compactMode: false,
    imageAspectRatio: 'square',
  },

  // Full control - everything enabled
  full: {
    showQuickActionsMenu: true,
    showStatusBadge: true,
    showRating: true,
    showGenres: true,
    showReadingDates: true,
    showISBN: true,
    showNotes: true,
    showNotesEditor: true,
    showSelectionIndicator: false,
    selectionIndicatorPosition: 'right',
    enforceUniformHeight: false,
    compactMode: false,
    imageAspectRatio: 'book',
  },

  // Roulette selection - clean, uniform, selectable
  roulette: {
    showQuickActionsMenu: false,
    showStatusBadge: true,
    showRating: false,
    showGenres: false,
    showReadingDates: false,
    showISBN: false,
    showNotes: false,
    showNotesEditor: false,
    showSelectionIndicator: true,
    selectionIndicatorPosition: 'left',
    enforceUniformHeight: true,
    compactMode: false,
    imageAspectRatio: 'square',
  },

  // Roulette result - display only, clean
  'roulette-result': {
    showQuickActionsMenu: false,
    showStatusBadge: true,
    showRating: true,
    showGenres: true,
    showReadingDates: false,
    showISBN: false,
    showNotes: true,
    showNotesEditor: false,
    showSelectionIndicator: false,
    selectionIndicatorPosition: 'right',
    enforceUniformHeight: false,
    compactMode: false,
    imageAspectRatio: 'book',
  },
}

// Helper function to get configuration for a context
export function getBookCardConfig(context: BookCardContext): BookCardConfig {
  return {
    context,
    actions: BOOK_CARD_ACTION_CONFIGS[context],
    layout: BOOK_CARD_LAYOUT_CONFIGS[context],
  }
}

// Helper function to check if a status transition is allowed
export function isStatusTransitionAllowed(config: BookCardActionConfig, toStatus: string): boolean {
  return config.allowedStatusTransitions.includes(toStatus)
}

// Helper function to get smart quick actions based on current status and context
export function getSmartQuickActions(
  book: Book,
  config: BookCardActionConfig
): Array<{ status: string; label: string; icon: string; color: string }> {
  if (!config.showQuickStatusActions) return []

  const actions: Array<{ status: string; label: string; icon: string; color: string }> = []

  switch (book.status) {
    case 'unread':
      if (config.allowedStatusTransitions.includes('reading')) {
        actions.push({
          status: 'reading',
          label: 'books.actions.startReadingToday',
          icon: 'clock',
          color: 'blue',
        })
      }
      // Only show wishlist option in 'full' context (special cases)
      if (config.allowedStatusTransitions.includes('wishlist')) {
        actions.push({
          status: 'wishlist',
          label: 'books.actions.moveToWishlist',
          icon: 'heart',
          color: 'purple',
        })
      }
      break

    case 'reading':
      if (config.allowedStatusTransitions.includes('read')) {
        actions.push({
          status: 'read',
          label: 'books.actions.markAsReadToday',
          icon: 'check',
          color: 'green',
        })
      }
      break

    case 'wishlist':
      // Special handling for wishlist - always show these actions regardless of allowedStatusTransitions
      // Primary action: Move to library as unread (acquired the book)
      actions.push({
        status: 'unread',
        label: 'books.actions.acquiredBook',
        icon: 'library',
        color: 'green',
      })
      // Alternative: Start reading immediately
      actions.push({
        status: 'reading',
        label: 'books.actions.startReadingNow',
        icon: 'clock',
        color: 'blue',
      })
      break

    case 'read':
      if (config.allowedStatusTransitions.includes('reading')) {
        actions.push({
          status: 'reading',
          label: 'books.actions.reread',
          icon: 'refresh',
          color: 'blue',
        })
      }
      break
  }

  return actions
}
